import { useQueryClient } from '@tanstack/react-query'
import { type ExternalLinkData, type ExternalLinkId, type ExternalLinkListInput, isNotDefined } from '@terros/common'
import { externalLinkKey, externalLinkListKey } from '../keys'
import { sortExternalLinks } from './util'

type ExternalLinkCache = {
  setItem: (data: ExternalLinkData) => void
  getItem: (externalLinkId: ExternalLinkId) => ExternalLinkData | undefined
  addItem: (data: ExternalLinkData) => void
  removeItem: (externalLinkId: ExternalLinkId) => void
  listItems: (props: ExternalLinkListInput) => ExternalLinkData[] | undefined
  invalidateList: (props: ExternalLinkListInput) => void
  setList: (props: ExternalLinkListInput, data: ExternalLinkData[]) => void
}

export function useExternalLinkCache(): ExternalLinkCache {
  const queryClient = useQueryClient()

  const setItem = (data: ExternalLinkData): void => {
    queryClient.setQueryData(externalLinkKey(data.externalLinkId), data)
    queryClient.setQueriesData({ queryKey: externalLinkListKey() }, (old: ExternalLinkData[] | undefined) =>
      old?.map((item) => (item.externalLinkId === data.externalLinkId ? data : item))
    )
  }

  const getItem = (externalLinkId: ExternalLinkId): ExternalLinkData | undefined => {
    return queryClient.getQueryData<ExternalLinkData>(externalLinkKey(externalLinkId))
  }

  const addItem = (data: ExternalLinkData): void => {
    queryClient.setQueryData(externalLinkKey(data.externalLinkId), data)
    queryClient.setQueriesData({ queryKey: externalLinkListKey() }, (old: ExternalLinkData[] | undefined) => {
      if (isNotDefined(old)) return
      const updated = [...old, data]
      return sortExternalLinks(updated)
    })
  }

  const removeItem = (externalLinkId: ExternalLinkId): void => {
    queryClient.removeQueries({ queryKey: externalLinkKey(externalLinkId) })
    queryClient.setQueriesData({ queryKey: externalLinkListKey() }, (previous: ExternalLinkData[] | undefined) => {
      if (!previous) return
      return previous.filter((link) => link.externalLinkId !== externalLinkId)
    })
  }

  const listItems = (props: ExternalLinkListInput): ExternalLinkData[] | undefined => {
    return queryClient.getQueryData<ExternalLinkData[]>(externalLinkListKey(props))
  }

  const invalidateList = (props: ExternalLinkListInput): void => {
    queryClient.invalidateQueries({ queryKey: externalLinkListKey(props) })
  }

  const setList = (props: ExternalLinkListInput, data: ExternalLinkData[]): void => {
    queryClient.setQueryData<ExternalLinkData[]>(externalLinkListKey(props), data)
  }

  return {
    setItem,
    getItem,
    addItem,
    removeItem,
    listItems,
    invalidateList,
    setList,
  }
}
